// deps
import { useCallback, useContext, useMemo } from "react";

// internal-deps
import { AddressApiResponse, RestaurantDetails, GiftingLocalCart } from "@swiggy-private/cart-types";
import { GlobalHeaderState, SwgyHeaderKeysList, getBridgeSdk } from "@swiggy-private/checkout-core";
import { isEmpty } from "@swiggy-private/js-utils";
import { Err, Ok, Result } from "@swiggy-private/js-fp";
import { captureError } from "@swiggy-private/core-ui";

// helpers
import {
    getAddressListFromAddressResponse,
    hasCartAddressSelectorXPDisabled,
    sortCartAddressList,
} from "@cart-client/helpers/CartAddress";
import { isGiftingCartSource } from "@cart-client/helpers/GiftingCart";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { CartFlowContext } from "@cart-client/contexts/CartFlowContext";
import { CartAddressContext } from "@cart-client/contexts/CartAddressContext";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

// apis
import { fetchNearByAddresses } from "@cart-client/apis/Cart";

// interfaces
import { ApiResponse } from "@cart-client/interfaces/Api";

// constants
import { FETCH_ADDRESS_LIST_SOURCE } from "@cart-client/constants/CartAddress";

export interface UseFetchAddressListReturnType {
    fetchAddressList: (source?: FETCH_ADDRESS_LIST_SOURCE) => Promise<void>;
    isCartAddressSelectorDisabled?: boolean;
    addNewAddress: () => Promise<void>;
    onAddOrSelectAddressClick: VoidFunction;
}

/**
 * Currently in food we fetch the nearby addresses using the restaurant details
 * But in gifting we don't have restaurant details in the cart data always (for IM only carts)
 * In IM cart today they don't fetch addresses on client but their checkout service sends it from backend
 * To reduce NFR requirements, we are using the cartLatLong from localCart to fetch the nearby addresses for IM cart in gifting
 */
function fetchGiftingCartAddressList(
    restaurantDetails?: RestaurantDetails,
    cartLatLong?: GiftingLocalCart["cartLatLong"],
): Promise<Result<AddressApiResponse, Error | ApiResponse>> {
    const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);
    const lat = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lat);
    const lng = GlobalHeaderState.getHeader(SwgyHeaderKeysList.lng);

    // restaurantDetails is not available in gifting if only IM cart is present
    const fetchLat = restaurantDetails?.lat ?? lat ?? cartLatLong?.lat;
    const fetchLng = restaurantDetails?.lng ?? lng ?? cartLatLong?.lng;

    if (isEmpty(fetchLat) || isEmpty(fetchLng)) {
        return Promise.resolve(Err(new Error("Lat or lng is empty")));
    }

    return fetchNearByAddresses(fetchLat, fetchLng, userId)
        .then((addressResponse) => {
            return Ok(addressResponse as AddressApiResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}

function fetchCartAddressList(
    restaurantDetails: RestaurantDetails,
    _?: GiftingLocalCart["cartLatLong"],
): Promise<Result<AddressApiResponse, Error | ApiResponse>> {
    const userId = GlobalHeaderState.getHeader(SwgyHeaderKeysList.userId);

    return fetchNearByAddresses(restaurantDetails.lat, restaurantDetails.lng, userId)
        .then((addressResponse) => {
            return Ok(addressResponse as AddressApiResponse);
        })
        .catch((err) => {
            return Err(err);
        });
}

const useFetchAddressList = (): UseFetchAddressListReturnType => {
    const { data } = useContext(CartFlowContext);
    const { cartState } = useContext(CartContext);
    const { updateCartInProgress, doUpdateCart } = useContext(UpdateCartContext);
    const { cartAddressState, setCartAddressState } = useContext(CartAddressContext);
    const { reviewedCart, localCart } = cartState;
    const { isFetchingAddressList, addressList } = cartAddressState;
    const isGiftingCart = isGiftingCartSource(data?.cartSource);

    const isCartAddressSelectorDisabled = useMemo(() => {
        if (isGiftingCartSource(data?.cartSource)) {
            // need to set true to allow API calls to fetch nearby addresses for gifting flows
            return true;
        }

        return hasCartAddressSelectorXPDisabled(reviewedCart?.data?.XPData);
    }, [data?.cartSource, reviewedCart?.data?.XPData]);

    const addNewAddress = useCallback(async () => {
        try {
            getBridgeSdk()
                .addNewAddress({ type: "generic", storeId: "" })
                .then(async (response) => {
                    setCartAddressState({
                        ...cartAddressState,
                        showAddressList: false,
                        addressList: [], //For re-fetching the addresses once we add an new address
                    });

                    if (response && response.addressId) {
                        if (!updateCartInProgress) {
                            await doUpdateCart({
                                ...localCart,
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                address_id: response.addressId.toString(),
                            });
                        }
                    }
                })
                .catch(() => void 0);
        } catch (err) {
            captureError(err);
        }
    }, [setCartAddressState, cartAddressState, updateCartInProgress, doUpdateCart, localCart]);

    const fetchAddressList = useCallback(
        async (source?: FETCH_ADDRESS_LIST_SOURCE): Promise<void> => {
            if (isFetchingAddressList) {
                return;
            }

            if (!isEmpty(addressList)) {
                setCartAddressState({
                    ...cartAddressState,
                    showAddressList: true,
                });
                return;
            }

            if (
                isEmpty(reviewedCart) ||
                isEmpty(reviewedCart.data) ||
                // if gifting cart, restaurant_details is not available in the cart data always
                (isEmpty(reviewedCart.data.restaurant_details) && !isGiftingCart)
            ) {
                setCartAddressState({
                    ...cartAddressState,
                    isInvalidCartData: true,
                });
                return;
            }

            setCartAddressState({
                ...cartAddressState,
                isFetchingAddressList: true,
                isErrorFetchingAddressList: false,
            });

            try {
                const fetchAddressCaller = isGiftingCart ? fetchGiftingCartAddressList : fetchCartAddressList;
                const data = await fetchAddressCaller(
                    reviewedCart.data.restaurant_details as RestaurantDetails,
                    localCart?.cartLatLong as GiftingLocalCart["cartLatLong"] | undefined,
                );

                if (data.isErr()) {
                    throw data;
                }
                const addressList = data
                    .map((addressData) => {
                        return getAddressListFromAddressResponse(addressData);
                    })
                    .unwrap();

                let sortedAddressList = addressList;

                try {
                    sortedAddressList = await sortCartAddressList(addressList);
                } catch (err) {
                    // no-op
                }

                setCartAddressState({
                    ...cartAddressState,
                    addressList: sortedAddressList,
                    isFetchingAddressList: false,
                    isErrorFetchingAddressList: false,
                    showAddressList: !isEmpty(addressList) || source !== FETCH_ADDRESS_LIST_SOURCE.ADD_OR_SELECT_CTA,
                });

                // go to add address if source is add or select address cta and address list is empty
                if (source === FETCH_ADDRESS_LIST_SOURCE.ADD_OR_SELECT_CTA && addressList.length === 0) {
                    addNewAddress();
                }
            } catch (err) {
                setCartAddressState({
                    ...cartAddressState,
                    isFetchingAddressList: false,
                    isErrorFetchingAddressList: true,
                });
            }
        },
        [
            isFetchingAddressList,
            addressList,
            reviewedCart,
            isGiftingCart,
            setCartAddressState,
            cartAddressState,
            localCart?.cartLatLong,
            addNewAddress,
        ],
    );

    const onAddOrSelectAddressClick = useCallback(() => {
        fetchAddressList(FETCH_ADDRESS_LIST_SOURCE.ADD_OR_SELECT_CTA);
    }, [fetchAddressList]);

    return {
        isCartAddressSelectorDisabled,
        fetchAddressList,
        addNewAddress,
        onAddOrSelectAddressClick,
    };
};

export default useFetchAddressList;
